import { makeDate_Std } from './date-helper';

/* ─────────────────────────────────────────────────────────────
   Safe date formatter
───────────────────────────────────────────────────────────── */
const safeDate = (val) => {
    if (!val) return '-';
    try { return makeDate_Std(val); } catch { return val; }
};


/* ─────────────────────────────────────────────────────────────
   CSS shared between the preview page and the PDF content
───────────────────────────────────────────────────────────── */
const CHECKLIST_CSS = `
  *, *::before, *::after { box-sizing: border-box; }
  body {
    font-family: Arial, sans-serif;
    margin: 0;
    padding: 0;
    background: #fff;
    color: #222;
  }
  .checklist-container {
    padding: 30px 30px 20px 30px;
    max-width: 100%;
  }
  .checklist-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
    margin-bottom: 20px;
  }
  .checklist-info {
    flex: 1;
    min-width: 0;
  }
  .checklist-info h3 {
    font-size: 1.35rem;
    margin: 0 0 4px 0;
    line-height: 1.3;
    word-break: break-word;
  }
  .checklist-info p {
    font-size: 0.9rem;
    margin: 3px 0;
    color: #555;
    line-height: 1.4;
  }
  .status-container {
    flex-shrink: 0;
    align-self: flex-start;
    border-radius: 5px;
    padding: 8px 16px;
    min-width: 120px;
    text-align: center;
  }
  .status-box {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    color: white;
    white-space: nowrap;
    line-height: 1.4;
  }
  .section {
    margin-bottom: 16px;
  }
  .section-header {
    background-color: #1f1f1f;
    border-radius: 6px;
    color: white;
    text-align: center;
    font-weight: bold;
    font-size: 0.95rem;
    padding: 7px 12px;
    line-height: 1.4;
    margin-bottom: 8px;
    width: 100%;
  }
  .section-row {
    display: flex;
    gap: 12px;
    margin-top: 6px;
  }
  .column {
    flex: 1;
    min-width: 0;
  }
  .item-card {
    padding: 12px 14px;
    border: 1px solid #dedede;
    border-radius: 5px;
    margin-bottom: 8px;
    background-color: #fafafa;
    page-break-inside: avoid;
    break-inside: avoid;
    line-height: 1.5;
  }
  .item-card h5 {
    margin: 0 0 6px 0;
    font-size: 0.9rem;
    color: #111;
  }
  .item-card p {
    margin: 3px 0;
    font-size: 0.85rem;
    color: #444;
  }
  .item-card small {
    color: #777;
  }
  .item-card img {
    display: block;
    width: 140px;
    height: 140px;
    object-fit: cover;
    border-radius: 5px;
    margin-top: 8px;
  }
  hr.section-divider {
    border: none;
    border-top: 2px solid #eee;
    margin: 20px 0 12px 0;
  }
`;

/* ─────────────────────────────────────────────────────────────
   fillChecklist
   Populates the template with checklist instance data.
   Returns a DOM Document.
───────────────────────────────────────────────────────────── */
export function fillChecklist(data) {
    const templateHtml = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <title>Checklist</title>
  <style>${CHECKLIST_CSS}</style>
</head>
<body>
  <div class="checklist-container">
    <div class="checklist-header">
      <div class="checklist-info">
        <h3 id="checklist-name">Checklist</h3>
        <p id="checklist-info">From <strong id="blueprint-name">-</strong> &bull; Created: <span id="date">-</span></p>
        <p id="task-id" style="display:none"></p>
        <p id="submitted-by">Submitted by <strong id="actioned-by-name">-</strong> at: <span id="actioned-at">-</span></p>
        <p id="approved-by"><span id="approved-by-name"></span></p>
        <p id="edited-by"><span id="edited-by-name"></span></p>
        <p>Checklist ID: <strong id="checklist_id">-</strong></p>
      </div>
      <div class="status-container" id="status-container">
        <div class="status-box" id="checklist-status">STATUS</div>
      </div>
    </div>
    <div id="sections-container"></div>
  </div>
</body>
</html>`;

    const doc = new DOMParser().parseFromString(templateHtml, 'text/html');

    // Title
    const rawTitle = data?.name ?? '';
    const checklistTitle = rawTitle.replace(/\s-\s\d{1,2}\s\w{3}\s\d{2}\s\d{1,2}:\d{2}[AP]M$/, '').trim();
    const titleEl = doc.getElementById('checklist-name');
    if (titleEl) titleEl.textContent = checklistTitle || 'Checklist';

    // ID
    const idEl = doc.getElementById('checklist_id');
    if (idEl) idEl.textContent = data?.id ?? '-';

    // Status
    const statusMap = { '-1': 'MISSED', 0: 'PENDING', 1: 'COMPLETED', 2: 'JOBS IN PROGRESS', 3: 'PENDING APPROVAL' };
    const colourMap = { '-1': '#c62828', 0: '#f9a825', 1: '#2e7d32', 2: '#1565c0', 3: '#e65100' };
    const statusText = statusMap[data?.status] ?? 'UNKNOWN';
    const statusEl = doc.getElementById('checklist-status');
    const statusContainerEl = doc.getElementById('status-container');
    if (statusEl) statusEl.textContent = statusText;
    if (statusContainerEl) statusContainerEl.style.backgroundColor = colourMap[data?.status] ?? '#757575';

    // Blueprint
    const bpEl = doc.getElementById('blueprint-name');
    if (bpEl) bpEl.textContent = data?.blueprint?.name ?? '-';

    // Created at
    const dateEl = doc.getElementById('date');
    if (dateEl) dateEl.textContent = safeDate(data?.created_at);

    // Task ID (missed CL)
    const taskIdEl = doc.getElementById('task-id');
    if (data?.meta?.task_id && taskIdEl) {
        taskIdEl.textContent = `Missed CL Task ID: ${data.meta.task_id}`;
        taskIdEl.style.display = '';
    }

    // Submitted by / at
    const submittedBy = data?.meta?.submitted_by_name ?? data?.actioned_by?.name;
    const submittedByEl = doc.getElementById('actioned-by-name');
    if (submittedBy && submittedByEl) submittedByEl.textContent = submittedBy;
    const actionedAtEl = doc.getElementById('actioned-at');
    if (actionedAtEl) actionedAtEl.textContent = safeDate(data?.meta?.submitted_at ?? data?.user_actioned_at ?? data?.updated_at);

    // Approved by
    const approvedBy = data?.meta?.approval?.approved_by_name;
    const approvedByEl = doc.getElementById('approved-by-name');
    const approvedByContainer = doc.getElementById('approved-by');
    if (approvedBy && approvedByEl) {
        approvedByEl.textContent = `Approved by ${approvedBy} at: ${safeDate(data?.meta?.approval?.approved_at)}`;
    } else if (approvedByContainer) {
        approvedByContainer.style.display = 'none';
    }

    // Edited by
    const editedBy = data?.meta?.edit_history?.user_name;
    const editedByEl = doc.getElementById('edited-by-name');
    const editedByContainer = doc.getElementById('edited-by');
    if (editedBy && editedByEl) {
        editedByEl.textContent = `Edited by ${editedBy} at: ${safeDate(data?.meta?.edit_history?.at)}`;
    } else if (editedByContainer) {
        editedByContainer.style.display = 'none';
    }

    // Sections + items
    const sectionsContainer = doc.getElementById('sections-container');
    if (sectionsContainer) sectionsContainer.innerHTML = '';

    // Precompute O(1) section name lookup — keys normalised to String
    // so Map lookups match regardless of whether ids arrive as int or string
    const sectionNameById = new Map(
        (data?.blueprint?.sections ?? []).map((s) => [String(s.id), s.name])
    );

    for (const item of data?.items ?? []) {
        const sectionId = item.pivot?.section;
        const sectionKey = sectionId != null ? String(sectionId) : null;
        const sectionName = (sectionKey && sectionNameById.get(sectionKey)) || 'General';
        const sectionContainerId = `section-${sectionId}`;
        let sectionContainer = doc.getElementById(sectionContainerId);

        if (!sectionContainer) {
            sectionContainer = doc.createElement('div');
            sectionContainer.id = sectionContainerId;
            sectionContainer.classList.add('section');
            const hr = doc.createElement('hr');
            hr.classList.add('section-divider');
            const sectionHeader = doc.createElement('div');
            sectionHeader.classList.add('section-header');
            sectionHeader.textContent = sectionName;
            sectionContainer.appendChild(hr);
            sectionContainer.appendChild(sectionHeader);
            sectionsContainer?.appendChild(sectionContainer);
        }

        // Item card
        const itemCard = doc.createElement('div');
        itemCard.classList.add('item-card');

        const itemNameEl = doc.createElement('h5');
        itemNameEl.textContent = item.name;
        itemCard.appendChild(itemNameEl);

        // Closing option — plain text, built with DOM nodes (no innerHTML)
        const closingOption = item.pivot?.meta?.closing_option ?? '';
        if (closingOption) {
            const optionLabel = doc.createElement('p');
            const optSmall = doc.createElement('small');
            optSmall.textContent = 'Status: ';
            optionLabel.appendChild(optSmall);
            optionLabel.appendChild(doc.createTextNode(String(closingOption).split(';').join(', ')));
            itemCard.appendChild(optionLabel);
        }

        // Remarks
        const comment = item.pivot?.meta?.com ?? '';
        const remarkEl = doc.createElement('p');
        const remSmall = doc.createElement('small');
        remSmall.textContent = 'Remarks: ';
        remarkEl.appendChild(remSmall);
        if (comment) {
            remarkEl.appendChild(doc.createTextNode(comment));
        } else {
            const em = doc.createElement('em');
            em.style.color = '#aaa';
            em.textContent = 'None';
            remarkEl.appendChild(em);
        }
        itemCard.appendChild(remarkEl);

        // Job IDs
        const linkedJobs = (data?.meta?.cli_task_id ?? []).filter((task) => +task?.item_id === +item.id && task?.req_id);
        const jobRows = linkedJobs.length
            ? linkedJobs.map((task) => [task.option ? `${task.option} → Corrective Job ID: ` : 'Corrective Job ID: ', task.req_id])
            : (item.pivot?.meta?.request_id ? [['Corrective Job ID: ', item.pivot.meta.request_id]] : []);
        const reportedJobs = item.pivot?.meta?.latest_similar_job_ids;
        const reportedRows = reportedJobs && typeof reportedJobs === 'object'
            ? Object.entries(reportedJobs).filter(([, reqId]) => reqId).map(([option, reqId]) => [`${option} → Already reported in Job ID: `, reqId])
            : (item.pivot?.meta?.latest_similar_job_id ? [['Already reported in Job ID: ', item.pivot.meta.latest_similar_job_id]] : []);
        [...jobRows, ...reportedRows].forEach(([label, value]) => {
            const p = doc.createElement('p');
            const s = doc.createElement('small');
            s.textContent = label;
            p.appendChild(s);
            p.appendChild(doc.createTextNode(String(value)));
            itemCard.appendChild(p);
        });

        // Approver comment
        const approvalComment = item.pivot?.meta?.approval_comment;
        if (approvalComment) {
            const approvalEl = doc.createElement('p');
            const appSmall = doc.createElement('small');
            const appStrong = doc.createElement('strong');
            appStrong.textContent = "Approver's Note: ";
            appSmall.appendChild(appStrong);
            approvalEl.appendChild(appSmall);
            approvalEl.appendChild(doc.createTextNode(approvalComment));
            itemCard.appendChild(approvalEl);
        }

        // Attached image
        if (item.pivot?.meta?.img) {
            const baseUrl = item.pivot.meta.img;
            const cacheBusted = `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}v=${Date.now()}`;
            const imgEl = doc.createElement('img');
            imgEl.crossOrigin = 'anonymous';
            imgEl.src = cacheBusted;
            imgEl.alt = 'Attached Image';
            itemCard.appendChild(imgEl);
        }

        // 2-column layout
        const rows = sectionContainer.querySelectorAll('.section-row');
        let row;
        if (rows.length === 0 || rows[rows.length - 1].children.length === 2) {
            row = doc.createElement('div');
            row.classList.add('section-row');
            sectionContainer.appendChild(row);
        } else {
            row = rows[rows.length - 1];
        }
        const col = doc.createElement('div');
        col.classList.add('column');
        col.appendChild(itemCard);
        row.appendChild(col);
    }

    return doc;
}

/* ─────────────────────────────────────────────────────────────
   buildStandaloneHtmlForPdf
   Returns a complete self-contained HTML page that auto-
   downloads the PDF when opened in a new tab.
   The html2pdf bundle is loaded from /html2pdf.bundle.min.js
   (copied to public/ during setup) so no CDN is needed.
───────────────────────────────────────────────────────────── */
export function buildStandaloneHtmlForPdf(data, filename, origin = '') {
    const doc = fillChecklist(data);
    const bodyHtml = doc.body.innerHTML;

    // Normalize to a safe string with a sensible default
    const name = String(filename ?? 'Checklist.pdf');
    // Escape for safe use in HTML attributes/content
    const safeFilenameHtml = name
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    // Use JSON.stringify so the filename is a safe JS string literal —
    // no quotes or backslashes in user data can break out of it
    const safeFilenameJs = JSON.stringify(name);

    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>${safeFilenameHtml}</title>
  <style>${CHECKLIST_CSS}</style>
</head>
<body>
<div id="checklist-content">
${bodyHtml}
</div>
<script src="${origin}/html2pdf.bundle.min.js"></script>
<script>
  (function () {
    var isIOS = /iPad|iPhone|iPod/i.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    function runPdfDownload() {
      if (typeof html2pdf === 'undefined') {
        alert('PDF library failed to load. Check your connection and try again.');
        return;
      }
      var btn = document.getElementById('pdf-download-btn');
      if (btn) { btn.disabled = true; btn.textContent = 'Generating…'; }
      html2pdf().set({
        margin: 0.3,
        filename: ${safeFilenameJs},
        image: { type: 'jpeg', quality: 0.9 },
        html2canvas: { scale: isIOS ? 1.5 : 2, useCORS: true, allowTaint: true, logging: false },
        jsPDF: { unit: 'in', format: 'a4', orientation: 'p' }
      }).from(document.getElementById('checklist-content')).save()
        .then(function () {
          if (!isIOS) { try { window.close(); } catch (e) {} }
          if (btn) { btn.disabled = false; btn.textContent = 'Download PDF'; }
        })
        .catch(function () {
          if (btn) { btn.disabled = false; btn.textContent = 'Download PDF'; }
          var msg = isIOS
            ? 'Could not create PDF. Try again or use Share from the browser menu.'
            : "Could not create PDF. Please try again or use your browser's print or save-as-PDF options.";
          alert(msg);
        });
    }
    function showIosDownloadUi() {
      var bar = document.createElement('div');
      bar.id = 'pdf-ios-bar';
      bar.setAttribute('style', 'position:fixed;bottom:0;left:0;right:0;padding:12px 16px;background:#1565c0;color:#fff;text-align:center;z-index:99999;font-family:system-ui,sans-serif;box-shadow:0 -2px 10px rgba(0,0,0,.2);');
      var b = document.createElement('button');
      b.id = 'pdf-download-btn';
      b.type = 'button';
      b.textContent = 'Download PDF';
      b.setAttribute('style', 'font-size:16px;padding:12px 24px;border-radius:8px;border:none;background:#fff;color:#1565c0;font-weight:600;cursor:pointer;');
      b.onclick = function () { runPdfDownload(); };
      var hint = document.createElement('p');
      hint.setAttribute('style', 'margin:8px 0 0;font-size:12px;opacity:.9;');
      hint.textContent = 'Tap the button below to download your checklist as a PDF.';
      bar.appendChild(b);
      bar.appendChild(hint);
      document.body.appendChild(bar);
    }
    function start() {
      if (isIOS) {
        showIosDownloadUi();
      } else {
        runPdfDownload();
      }
    }
    if (document.readyState === 'complete') { start(); }
    else { window.addEventListener('load', start); }
  })();
</script>
</body>
</html>`;
}
